import { z } from 'zod'
import { DateTimeStringSchema } from './common'
import { GiftCodeDataSchema, PrizeHistoryDataSchema } from './reward'

/** PrizeSummaryData: 一覧API で返される賞品サマリー */
export const PrizeSummaryDataSchema = z.object({
  id: z.number(),
  name: z.string(),
  type: z.enum(['ITEM', 'DLC', 'AR_PHOTO', 'COUPON', 'GIFT_CODE', 'GIFTEE']),
  fulfillmentType: z.string(),
  contentFile: z.string().nullable(),
  /**
   * ギフトコード情報（type=GIFT_CODE かつ status=EXCHANGED の場合のみ返される）。
   * ACHIEVED 状態では返されない。
   */
  code: GiftCodeDataSchema.nullable().optional(),
  /** クーポン画像URL（詳細API が PrizeData を返す場合のみ。COUPON タイプのみ） */
  image: z.string().nullable().optional(),
  /**
   * クーポン使用開始日。"YYYY/MM/DD" 形式（詳細APIが PrizeData を返す場合のみ）。
   * 一覧APIでは返されないためoptional。
   */
  startAt: z
    .string()
    .regex(/^\d{4}\/\d{2}\/\d{2}$/, '"YYYY/MM/DD" 形式で入力してください')
    .nullable()
    .optional(),
  /**
   * クーポン使用終了日。"YYYY/MM/DD" 形式（詳細APIが PrizeData を返す場合のみ）。
   * 一覧APIでは返されないためoptional。
   */
  endAt: z
    .string()
    .regex(/^\d{4}\/\d{2}\/\d{2}$/, '"YYYY/MM/DD" 形式で入力してください')
    .nullable()
    .optional(),
})
export type PrizeSummaryData = z.infer<typeof PrizeSummaryDataSchema>

export const UserProfileDataSchema = z.object({
  id: z.number(),
  username: z.string().nullable(),
  email: z.string().nullable(),
  nickname: z.string().nullable(),
  gender: z.string().nullable(),
  birthday: z.string().nullable(),
  zipCode: z.string().nullable(),
  area: z.string().nullable(),
  age: z.string().nullable(),
  round: z.number().nullable(),
  status: z.number().nullable(),
  recoveryCode: z.string().nullable(),
  createdAt: DateTimeStringSchema.nullable(),
  updatedAt: DateTimeStringSchema.nullable(),
})

export const UserRewardDataSchema = z.object({
  id: z.number(),
  presentId: z.number().nullable(),
  status: z.number().nullable(),
  createdAt: DateTimeStringSchema.nullable(),
  updatedAt: DateTimeStringSchema.nullable(),
})

const SpotInStampDataSchema = z.object({
  id: z.number(),
  name: z.string(),
  image: z.string().nullable(),
})

/**
 * スタンプ獲得不可の理由コード。
 * 獲得API `POST /api/v2/stamps/{id}` が返す `error.code` と同じ語彙・同じ判定順序。
 */
export const StampUnavailableReasonSchema = z.enum([
  'CAMPAIGN_PERIOD_OUT',
  'STAMP_PERIOD_OUT',
  'STAMP_DAY_OUT',
  'STAMP_HOURS_OUT',
  'STAMP_NOT_AVAILABLE',
  'SHEET_LOCKED',
  'STAMP_ALREADY_ACQUIRED',
  'STAMP_ALREADY_ACQUIRED_TODAY',
])
export type StampUnavailableReason = z.infer<typeof StampUnavailableReasonSchema>

export const UserStampDataSchema = z.object({
  id: z.number(),
  name: z.string(),
  image: z.string().nullable(),
  spots: z.array(SpotInStampDataSchema),
  /**
   * このスタンプ自体を取得済みか。
   * ボタンの活性/非活性には使わず isAvailable / unavailableReason を参照すること
   * （1日1回制限などで「未取得だが今は押せない」状態があるため）。
   */
  isAcquired: z.boolean(),
  acquiredCount: z.number(),
  /** 今この瞬間に獲得可能か。フィールド未対応のバックエンドに対しては従来どおり true 扱いにフォールバックする */
  isAvailable: z.boolean().optional().default(true),
  /** 獲得不可の理由。バックエンドが新コードを追加しても parse を壊さないよう未知値は null に縮退させる */
  unavailableReason: StampUnavailableReasonSchema.nullable().optional().default(null).catch(null),
  /**
   * 現在の期・周回で最後に獲得したとき付いたポイント（付与時に確定した実際の値。管理者補填を含む・#3815）。
   * 再表示ダイアログの pt 表示に使う。シート一覧（GET /api/v2/sheets）の stamps[].point は今の設定値で実績とずれ得るため使わない。
   * 0pt の獲得は 0 のまま返る（GET /api/v2/user/sheets の history[].point は 0 を null で返すのと異なる）。
   * 未獲得なら null。項目のない旧バックエンドの応答も null に倒し、pt を出さない（画像にフォールバック）。
   * 表示専用の項目なので、想定外の値（小数など）でも UserStampData 全体の parse を壊さず null に縮退させる
   * （parse が落ちると isAcquired ごと失われ、獲得済みのオーバーレイや再表示導線が未獲得に戻るため）。
   */
  latestAcquiredPoint: z.number().int().nullable().optional().default(null).catch(null),
  /**
   * 現在の期・周回で付いたポイントの累計（acquiredCount と同じ範囲・付与時の実際の値・#3815）。未獲得なら 0。
   * 特典交換で消費したポイントは差し引かない。現状の画面では表示しない（再表示ダイアログを累計表示に変える場合に使う）。
   * 旧バックエンドの応答と 0 を区別するため default を付けない。latestAcquiredPoint と同じく、想定外の値は undefined に縮退させる。
   */
  totalAcquiredPoint: z.number().int().optional().catch(undefined),
})

/**
 * GET /api/v2/user の user.previousLogin（#3598）。
 * 「今のセッション以外で一番新しいログイン」。初回ログイン・ゲスト・履歴なしは null。
 */
export const PreviousLoginSchema = z.object({
  /** ログイン日時（JST・+09:00 付き ISO 8601）。表示はタイムゾーン変換しない */
  loggedInAt: DateTimeStringSchema,
  /**
   * ログイン方法（LINE / GOOGLE / APPLE / FACEBOOK / YAHOO_JAPAN / EMAIL_OTP / EMAIL_PASSWORD_OTP /
   * CUSTOM_ID / REGISTER / GUEST / UNKNOWN）。バックエンドが値を増やしても parse を壊さないよう enum にしない。
   * 現状の画面では表示しない。
   */
  method: z.string(),
  /**
   * 端末の概要（例: "iPhone / Safari"）。判定できない・記録が無い（リリース前のログイン）場合は null。
   * 現状の画面では表示しない（#3635 で日時だけを出すと決定）。
   */
  device: z.string().nullable(),
})
export type PreviousLogin = z.infer<typeof PreviousLoginSchema>

/** GET /api/v2/user レスポンスの user オブジェクト */
export const UserGetProfileSchema = z.object({
  id: z.number(),
  nickname: z.string().nullable(),
  recoveryCode: z.string().nullable(),
  createdAt: DateTimeStringSchema.nullable(),
  /**
   * 前回のログイン（#3598）。旧バックエンド（ロールバック時）はキー自体を返さないため optional + default で null に倒す
   * （必須にするとメニューの id 表示ごと落ちる）。
   */
  previousLogin: PreviousLoginSchema.nullable().optional().default(null),
})
export type UserGetProfile = z.infer<typeof UserGetProfileSchema>

export const UserGetResponseSchema = z.object({
  user: UserGetProfileSchema,
})
export type UserGetResponse = z.infer<typeof UserGetResponseSchema>

export const UserRewardGetResponseSchema = z.object({
  reward: UserRewardDataSchema.nullable(),
})

export const UserRewardListResponseSchema = z.object({
  rewards: z.array(UserRewardDataSchema),
})

export const UserStampGetResponseSchema = z.object({
  stamp: UserStampDataSchema.nullable(),
})

export const UserStampListResponseSchema = z.object({
  stamps: z.array(UserStampDataSchema),
})

/**
 * GET /api/v2/user/rewards レスポンスの条件進捗データ。
 *
 * conditions[] には末端条件（STAMP_COUNT / POINT_SUM 等）のみが入り、
 * 選択グループ（CONDITION_GROUP）の行は含まれない。グループの進捗は
 * RewardConditionGroupProgressDataSchema（conditionGroups[]）を参照すること。
 */
export const RewardConditionProgressDataSchema = z.object({
  displayName: z.string().nullable(),
  type: z.enum(['STAMP_COUNT', 'POINT_SUM', 'LOGIN_DAYS', 'USER_STATUS', 'QUEST_COMPLETE', 'CONDITION_GROUP']),
  current: z.number().int(),
  threshold: z.number().int(),
  /**
   * この条件が属する選択グループの条件ID（#3576）。
   * conditionGroups[].id と対応し、グループに属さないトップレベル条件は null。
   * 旧バックエンドとの互換のため optional にしている。
   */
  groupId: z.number().int().nullable().optional().default(null),
})

/**
 * 獲得条件の選択グループとその進捗（#3576 / #3581）。
 *
 * conditions[] は末端条件を平坦に並べるため「9コースのうち2コース達成でよい」といった
 * まとまりを表現できない。グループを正規化したこの配列を conditions[].groupId から引くことで、
 * フロント側でツリー構造を復元する。
 *
 * 並び順はプレオーダー（親 → 子孫）でバックエンドが保証している。
 */
export const RewardConditionGroupProgressDataSchema = z.object({
  /** グループ条件のID（conditions[].groupId と対応） */
  id: z.number().int(),
  /** 親グループのID。トップレベルのグループは null */
  parentId: z.number().int().nullable(),
  /** グループ表示名（多言語対応）。管理画面で未設定なら null */
  displayName: z.string().nullable(),
  /** 子条件の結合方法。AND = すべて満たす / OR = いずれか threshold 件を満たす */
  operator: z.enum(['AND', 'OR']),
  /** 達成済みの子条件の数（分子）。OR では threshold を超えることがある */
  current: z.number().int(),
  /** 達成が必要な子条件の数（分母）。AND は子条件の総数 */
  threshold: z.number().int(),
})

/**
 * 特典の獲得状況ステータス。一覧（GET /api/v2/user/rewards）と
 * 詳細（GET /api/v2/user/rewards/{id}）で同一の列挙を返すため、単一の定義を共有する。
 *
 * - UNOBTAINED  獲得条件が未達成（これから獲得できる）
 * - ACHIEVABLE  条件達成済みで獲得可能
 * - ACHIEVED / EXCHANGED / USED / UNAPPLIED / APPLIED  自分のトランザクションが存在する状態
 * - LOST        抽選ハズレ
 * - ENDED / SOLD_OUT  獲得期間終了・在庫切れ
 * - UNAVAILABLE SELECT_ONE グループの選択枠を他特典が使い切っており、二度と獲得できない（#3495）
 *
 * UNAPPLIED / APPLIED は詳細 API のみ返すが、列挙自体は一覧・詳細で共通。
 */
export const UserRewardAcquisitionStatusSchema = z.enum([
  'UNOBTAINED',
  'ACHIEVABLE',
  'ACHIEVED',
  'EXCHANGED',
  'USED',
  'UNAPPLIED',
  'APPLIED',
  'LOST',
  'ENDED',
  'SOLD_OUT',
  'UNAVAILABLE',
])

/** GET /api/v2/user/rewards レスポンスの認証済みユーザー特典獲得状況データ */
export const UserRewardStatusDataSchema = z.object({
  id: z.number(),
  /** 獲得トランザクションID（user_reward_transactions.id）。ACHIEVED/EXCHANGED/USED の場合のみ値が入る */
  transactionId: z.number().nullable(),
  name: z.string().nullable(),
  image: z.string().nullable(),
  /** 獲得状況。値の意味は UserRewardAcquisitionStatusSchema を参照 */
  status: UserRewardAcquisitionStatusSchema,
  /**
   * 賞品選択方式（SINGLE / GIVE_ALL / USER_SELECT / LOTTERY）。
   * SINGLE: 賞品が1つに確定しているため、ACHIEVED 時に再 acquire せず直接 exchange できる。
   */
  prizeSelectionType: z.enum(['SINGLE', 'GIVE_ALL', 'USER_SELECT', 'LOTTERY']).nullable().optional(),
  conditions: z.array(RewardConditionProgressDataSchema),
  /**
   * 獲得条件の選択グループ一覧（#3576）。グループが無い特典では空配列。
   * 旧バックエンドとの互換のため optional + default にしている
   * （必須にするとバックエンドのロールバック時にパースエラーで特典一覧が落ちる）。
   */
  conditionGroups: z.array(RewardConditionGroupProgressDataSchema).optional().default([]),
  createdAt: DateTimeStringSchema.nullable(),
  /**
   * 賞品サマリー一覧（一覧API /api/v2/user/rewards のみ返す。詳細APIでは返さない）。
   * 一覧APIでは PrizeSummaryData（code・applicationFormなし）の配列が返る。
   * 詳細APIでの prizes（PrizeHistoryData[]）は UserRewardStatusDetailDataSchema を参照。
   */
  prizes: z.array(PrizeSummaryDataSchema).optional().default([]),
})

/** GET /api/v2/user/rewards — 一覧レスポンス */
export const UserRewardStatusListResponseSchema = z.object({
  rewards: z.array(UserRewardStatusDataSchema),
})

/**
 * ユーザー特典詳細レスポンス用データ（詳細API専用）。
 * UserRewardStatusData との違い:
 * - prizes が PrizeHistoryData[]（獲得試行履歴。当選・ハズレ両方を含む）
 * - isRepeatable フィールドを持つ（複数回獲得可能フラグ）
 */
export const UserRewardStatusDetailDataSchema = z.object({
  id: z.number(),
  transactionId: z.number().nullable(),
  name: z.string().nullable(),
  image: z.string().nullable(),
  /** 獲得状況。一覧と同一の列挙を共有する */
  status: UserRewardAcquisitionStatusSchema,
  prizeSelectionType: z.enum(['SINGLE', 'GIVE_ALL', 'USER_SELECT', 'LOTTERY']).nullable().optional(),
  conditions: z.array(RewardConditionProgressDataSchema),
  /** 獲得条件の選択グループ一覧（#3576）。一覧APIと同一の契約 */
  conditionGroups: z.array(RewardConditionGroupProgressDataSchema).optional().default([]),
  createdAt: DateTimeStringSchema.nullable(),
  prizes: z.array(PrizeHistoryDataSchema).optional().default([]),
  isRepeatable: z.boolean().optional().default(false),
})

/** GET /api/v2/user/rewards/{id} — 単件レスポンス */
export const UserRewardStatusGetResponseSchema = z.object({
  reward: UserRewardStatusDetailDataSchema.nullable(),
  // PR #2609: ルートの isLotteryLost / userRewardTransactionId は除去済み。
  // ハズレ判定は reward.prizes[].isLotteryLost を参照すること。
})

export type UserProfileData = z.infer<typeof UserProfileDataSchema>
export type UserRewardData = z.infer<typeof UserRewardDataSchema>
export type UserStampData = z.infer<typeof UserStampDataSchema>
export type UserRewardGetResponse = z.infer<typeof UserRewardGetResponseSchema>
export type UserRewardListResponse = z.infer<typeof UserRewardListResponseSchema>
export type UserStampGetResponse = z.infer<typeof UserStampGetResponseSchema>
export type UserStampListResponse = z.infer<typeof UserStampListResponseSchema>
export type RewardConditionProgressData = z.infer<typeof RewardConditionProgressDataSchema>
export type RewardConditionGroupProgressData = z.infer<typeof RewardConditionGroupProgressDataSchema>
export type UserRewardAcquisitionStatus = z.infer<typeof UserRewardAcquisitionStatusSchema>
export type UserRewardStatusData = z.infer<typeof UserRewardStatusDataSchema>
export type UserRewardStatusDetailData = z.infer<typeof UserRewardStatusDetailDataSchema>
export type UserRewardStatusListResponse = z.infer<typeof UserRewardStatusListResponseSchema>
export type UserRewardStatusGetResponse = z.infer<typeof UserRewardStatusGetResponseSchema>

/** GET /api/v2/user/activities — スタンプ履歴データ */
export const StampActivityDataSchema = z.object({
  id: z.number().nullable(),
  name: z.string().nullable(),
  image: z.string().nullable(),
  spotName: z.string().nullable(),
  /** このスタンプが属する有効シートの種別ユニーク一覧 */
  sheetTypes: z.array(z.enum(['stamp_rally', 'point_rally', 'point_card', 'overlay'])).default([]),
})

/** GET /api/v2/user/activities — 特典履歴データ */
export const RewardActivityDataSchema = z.object({
  transactionId: z.number(),
  name: z.string().nullable(),
  status: z.enum(['ACHIEVED', 'EXCHANGED', 'USED', 'UNAPPLIED', 'APPLIED', 'LOST']),
  prizeName: z.string().nullable(),
  prizeType: z.enum(['ITEM', 'DLC', 'AR_PHOTO', 'COUPON', 'GIFT_CODE', 'GIFTEE']).nullable(),
  image: z.string().nullable(),
})

/** GET /api/v2/user/activities — クーポン履歴データ */
export const CouponActivityDataSchema = z.object({
  id: z.number(),
  name: z.string().nullable(),
  spotName: z.string().nullable(),
  image: z.string().nullable(),
})

/** GET /api/v2/user/activities — 行動履歴1件 */
export const ActivityItemSchema = z.object({
  type: z.enum(['STAMP', 'REWARD', 'COUPON']),
  activityAt: z.string(),
  stamp: StampActivityDataSchema.nullable(),
  reward: RewardActivityDataSchema.nullable(),
  coupon: CouponActivityDataSchema.nullable(),
})

/** GET /api/v2/user/activities — レスポンス */
export const UserActivitiesResponseSchema = z.object({
  activities: z.array(ActivityItemSchema),
  nextCursor: z.string().nullable(),
  hasMore: z.boolean(),
})

export type StampActivityData = z.infer<typeof StampActivityDataSchema>
export type RewardActivityData = z.infer<typeof RewardActivityDataSchema>
export type CouponActivityData = z.infer<typeof CouponActivityDataSchema>
export type ActivityItem = z.infer<typeof ActivityItemSchema>
export type UserActivitiesResponse = z.infer<typeof UserActivitiesResponseSchema>
